import React, {
    useState,
    cloneElement,
    useMemo,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
} from "react";
import "./index.css";

// Tabs now supports both controlled and uncontrolled usage.
// - Uncontrolled: pass `defaultValue`
// - Controlled: pass `value` and `onValueChange`
export function Tabs({ children, defaultValue, value, onValueChange }) {
    const [internalActive, setInternalActive] = useState(defaultValue);
    const activeTab = value !== undefined ? value : internalActive;
    const setActiveTab = useMemo(
        () => (onValueChange ? onValueChange : setInternalActive),
        [onValueChange]
    );

    return (
        <>
            {React.Children.map(children, (child) => {
                if (!React.isValidElement(child)) {
                    return child;
                }
                return child.type === TabsList || child.type === TabsContent
                    ? cloneElement(child, { activeTab, setActiveTab })
                    : child;
            })}
        </>
    );
}

export function TabsList({
    children,
    activeTab,
    setActiveTab,
    as: Wrapper,
    className,
    listClassName,
    ...rest
}) {
    const listRef = useRef(null);
    const indicatorFrameRef = useRef(null);
    const hasMeasuredIndicatorRef = useRef(false);
    const [indicatorStyle, setIndicatorStyle] = useState({
        height: 0,
        opacity: 0,
        width: 0,
        x: 0,
        y: 0,
    });
    const triggerCount = React.Children.toArray(children).length;

    const updateIndicator = useCallback(() => {
        const listNode = listRef.current;
        if (!listNode) {
            return;
        }

        const triggerNodes = Array.from(listNode.querySelectorAll(".tabs-trigger"))
            .filter((node) => node.parentElement === listNode);
        const activeNode = triggerNodes.find((node) => node.classList.contains("active"));
        if (!(activeNode instanceof HTMLElement)) {
            setIndicatorStyle((currentStyle) => ({
                ...currentStyle,
                opacity: 0,
            }));
            return;
        }

        const nextStyle = {
            height: activeNode.offsetHeight,
            opacity: activeNode.offsetWidth > 0 && activeNode.offsetHeight > 0 ? 1 : 0,
            width: activeNode.offsetWidth,
            x: activeNode.offsetLeft,
            y: activeNode.offsetTop,
        };

        setIndicatorStyle((currentStyle) => {
            if (
                currentStyle.height === nextStyle.height
                && currentStyle.opacity === nextStyle.opacity
                && currentStyle.width === nextStyle.width
                && currentStyle.x === nextStyle.x
                && currentStyle.y === nextStyle.y
            ) {
                return currentStyle;
            }
            return nextStyle;
        });
    }, []);

    const scheduleIndicatorUpdate = useCallback(() => {
        if (typeof window === "undefined") {
            updateIndicator();
            return;
        }

        if (indicatorFrameRef.current != null) {
            window.cancelAnimationFrame(indicatorFrameRef.current);
        }

        indicatorFrameRef.current = window.requestAnimationFrame(() => {
            indicatorFrameRef.current = null;
            updateIndicator();
        });
    }, [updateIndicator]);

    useLayoutEffect(() => {
        if (!hasMeasuredIndicatorRef.current) {
            updateIndicator();
            hasMeasuredIndicatorRef.current = true;
            return;
        }

        scheduleIndicatorUpdate();
    }, [activeTab, scheduleIndicatorUpdate, updateIndicator]);

    useEffect(() => {
        const listNode = listRef.current;
        if (!listNode) {
            return undefined;
        }

        const resizeObserver = typeof ResizeObserver === "function"
            ? new ResizeObserver(() => scheduleIndicatorUpdate())
            : null;

        if (resizeObserver) {
            resizeObserver.observe(listNode);
            listNode.querySelectorAll(".tabs-trigger").forEach((node) => {
                if (node instanceof HTMLElement) {
                    resizeObserver.observe(node);
                }
            });
        }

        scheduleIndicatorUpdate();
        window.addEventListener("resize", scheduleIndicatorUpdate);

        return () => {
            if (indicatorFrameRef.current != null) {
                window.cancelAnimationFrame(indicatorFrameRef.current);
                indicatorFrameRef.current = null;
            }
            resizeObserver?.disconnect();
            window.removeEventListener("resize", scheduleIndicatorUpdate);
        };
    }, [scheduleIndicatorUpdate, triggerCount]);

    const combinedListClassName = ["tabs-list", listClassName, !Wrapper && className]
        .filter(Boolean)
        .join(" ")
        .trim();

    const listProps = Wrapper ? {} : rest;

    const content = (
        <div className={combinedListClassName} ref={listRef} {...listProps}>
            <span
                className={`tabs-list__indicator${indicatorStyle.opacity ? " tabs-list__indicator--ready" : ""}`}
                style={{
                    height: `${indicatorStyle.height}px`,
                    transform: `translate3d(${indicatorStyle.x}px, ${indicatorStyle.y}px, 0)`,
                    width: `${indicatorStyle.width}px`,
                }}
            />
            {React.Children.map(children, (child) =>
                cloneElement(child, { activeTab, setActiveTab })
            )}
        </div>
    );

    if (!Wrapper) {
        return content;
    }

    const wrapperProps = rest;
    return (
        <Wrapper className={className} {...wrapperProps}>
            {content}
        </Wrapper>
    );
}

export function TabsTrigger({ value, activeTab, setActiveTab, children }) {
    return (
        <button
            type="button"
            className={`tabs-trigger ${activeTab === value ? "active" : ""}`}
            onClick={() => setActiveTab(value)}
        >
            {children}
        </button>
    );
}

export function TabsContent({ value, activeTab, children }) {
    return activeTab === value ? <>{children}</> : null;
}
